import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AccordionProps,
  Box,
  colors,
  Heading,
  HStack,
  LinkBox,
} from "@biblioteksentralen/react";
import { sortAlphabetical } from "@biblioteksentralen/utils";
import { digitalLibraryServiceCategories } from "@libry-content/common";
import { sum } from "radash";
import { getPath } from "@libry-content/common";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { useSitePalette } from "../../utils/useSitePalette";
import { DigitalLibraryServicePreviewMobile } from "./DigitalLibraryServicePreviewMobile";
import { LibraryAppPreview } from "./LibraryAppPreview";
import { ServiceCategoryIcon } from "./ServiceCategoryIcon";
import { CategoryWithDigitalLibraryServices, isLibraryAppCategory } from "./utils";
import React from "react";

type ServiceCategoryDesktopProps = AccordionProps & {
  servicesByCategory: CategoryWithDigitalLibraryServices[];
};

export const DigitalLibraryServiceCategoriesMobile = ({
  servicesByCategory,
  ...accordionProps
}: ServiceCategoryDesktopProps) => {
  const { ts } = useTranslation();
  const palette = useSitePalette();

  const nCategories = servicesByCategory.length;
  const nServices = sum(servicesByCategory.map(([, services]) => services.length));
  const accordionsInitiallyOpen = nCategories <= 3 && nServices <= 3;

  return (
    <Accordion
      as="ul"
      allowMultiple
      listStyleType="none"
      defaultIndex={accordionsInitiallyOpen ? [...Array(nServices)].map((_, index) => index) : undefined}
      {...accordionProps}
    >
      {servicesByCategory.map(([category, services]) => {
        if (!services[0]) return null;

        if (isLibraryAppCategory(category)) {
          return (
            <React.Fragment key="services">
              <Heading as="h2" fontSize="2xl" marginBottom="1rem" display={{ base: "none", sm: "block" }} id={category}>
                {ts(digitalLibraryServiceCategories[category])}
              </Heading>
              {services.map((service) => (
                <LinkBox as="li" key={`${category}_${service._type}-mobile`}>
                  <LibraryAppPreview digitalLibraryService={service} />
                </LinkBox>
              ))}
            </React.Fragment>
          );
        }

        return (
          <AccordionItem
            as="li"
            key={`${category}-mobile`}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            border={`1px solid ${colors.grey30}`}
            marginBottom="1rem"
            borderRadius="lg"
            padding="1rem 1rem 0"
          >
            <AccordionButton
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              _hover={{}}
              padding="0 0 1rem"
              /* Hide focus when not navigating by keyboard */
              sx={{
                ":focus:not(:focus-visible)": {
                  outline: 0,
                  boxShadow: "none",
                },
              }}
            >
              <HStack justifyContent="flex-start" spacing="1rem">
                <ServiceCategoryIcon flexShrink={0} {...palette.colors.lightaccent4.css} category={category} />
                <Heading as="h2" fontSize="lg" textAlign="left">
                  {ts(digitalLibraryServiceCategories[category])}
                </Heading>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
            <Box width="100%">
              <AccordionPanel as="ul" padding="0" borderTop={`1px solid ${colors.grey30}`} listStyleType="none">
                {sortAlphabetical(services, ({ name }) => ts(name) ?? "z").map((digitalLibraryService) => {
                  const href = getPath(digitalLibraryService);

                  if (!digitalLibraryService || !href) return null;

                  return (
                    <LinkBox
                      key={`${digitalLibraryService._id}-mobile`}
                      as="li"
                      width="100%"
                      padding="1.5rem 0"
                      borderBottom={`1px solid ${colors.grey30}`}
                      _last={{ borderBottom: "none" }}
                    >
                      <DigitalLibraryServicePreviewMobile
                        digitalLibraryService={digitalLibraryService}
                        href={href}
                        category={category}
                      />
                    </LinkBox>
                  );
                })}
              </AccordionPanel>
            </Box>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};
